import type { ColumnDef, RowData } from '@tanstack/react-table'
import type { DataTableFeatures } from './dataTableFeatures'
import { DATA_TABLE_DEFAULT_COLUMN_SIZE, type DataTableColumn } from './types'

const resolveColumnSize = <T extends RowData>(
  column: DataTableColumn<T>
): number => column.size ?? column.defaultSize ?? DATA_TABLE_DEFAULT_COLUMN_SIZE

export const defineColumns = <T extends RowData>(
  columns: DataTableColumn<T>[]
): ColumnDef<DataTableFeatures, T, unknown>[] =>
  columns.map((column) => {
    const size = resolveColumnSize(column)
    const base: ColumnDef<DataTableFeatures, T, unknown> = {
      id: column.id,
      header: column.header,
      enableSorting: column.enableSorting,
      enableColumnFilter: column.enableColumnFilter,
      enableResizing: column.enableResizing ?? true,
      enableHiding: column.enableHiding,
      size,
      minSize: column.minSize ?? Math.min(size, 80),
      maxSize: column.maxSize,
      meta: column.meta
    }

    const wrapCell = (
      cell: NonNullable<DataTableColumn<T>['cell']>
    ): ColumnDef<DataTableFeatures, T, unknown>['cell'] => {
      return ({ row, getValue, table }) =>
        cell({
          row: row.original,
          value: getValue(),
          filterByValue: (value) =>
            table.options.meta?.onColumnFilterClick?.(column.id, value),
          isExpanded: row.getIsExpanded(),
          toggleExpanded: () => row.toggleExpanded()
        })
    }

    if (column.accessor === undefined) {
      return {
        ...base,
        cell: column.cell ? wrapCell(column.cell) : undefined
      }
    }

    const accessorFn =
      typeof column.accessor === 'function'
        ? column.accessor
        : (row: T) => row[column.accessor as keyof T]

    return {
      ...base,
      accessorFn,
      cell: column.cell ? wrapCell(column.cell) : undefined
    }
  })
